// Money conversion. Canonical types carry integer cents; SSM's API takes
// dollars as a number. The conversion happens exactly here so no mapper has to
// remember it.

/** Integer cents -> dollars, e.g. 12345 -> 123.45. Rejects fractional cents. */
export function centsToDollars(cents: number): number {
  if (!Number.isFinite(cents) || !Number.isInteger(cents)) {
    throw new Error(`centsToDollars: expected integer cents, got ${String(cents)}`);
  }
  if (cents < 0) {
    throw new Error(`centsToDollars: refusing negative amount ${String(cents)}`);
  }
  return cents / 100;
}

/** Dollars -> integer cents. Rounds to the nearest cent; rejects negatives. */
export function dollarsToCents(dollars: number): number {
  if (!Number.isFinite(dollars)) {
    throw new Error(`dollarsToCents: expected a finite number, got ${String(dollars)}`);
  }
  if (dollars < 0) {
    throw new Error(`dollarsToCents: refusing negative amount ${String(dollars)}`);
  }
  return Math.round(dollars * 100);
}

/**
 * Parses a money string the app might hold ("50", "$50.00", "1,250") into
 * integer cents. Returns null when there is no number to find — callers must
 * decide what that means rather than silently posting 0 to a facility's books.
 */
export function parseDollarString(value: string | null | undefined): number | null {
  if (value == null) return null;
  const cleaned = String(value).replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const match = /-?\d+(\.\d+)?/.exec(cleaned);
  if (!match) return null;
  const parsed = Number(match[0]);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}
